import { useEffect } from "react";
import { useImmer } from "use-immer";
import { useDropdownApis } from "../../../shared";
import { errorMessage, successMessage } from "../../../utils";
import { assignStoreToCoupons, deleteStoreFromCoupons } from "../../Coupons/api";

// RACT is a coupon, so its stores come from the RACT details and are
// updated through the coupon store APIs using the coupon id.
export const useRACTStores = ({ couponId, stores, onRefresh }) => {
  const { storeList: allStores } = useDropdownApis({ isStoreList: true });

  const [state, setState] = useImmer({
    isOpen: false,
    filteredStores: [],
    selectedStores: [],
    isSaveButton: false,
    isDelete: false,
    isDeleteButton: false,
    storeId: null,
  });

  useEffect(() => {
    HandleFilterStores("initial");
  }, [allStores, stores]);

  // Stores already linked to RACT are excluded from the add dialog.
  const HandleFilterStores = (type, text = "") => {
    const alreadyStores = (stores ?? []).map((store) => store.id);
    const availableStores = (allStores ?? []).filter(
      (store) => !alreadyStores.includes(store.id),
    );
    const searchText = text.toLowerCase();

    setState((draft) => {
      draft.filteredStores =
        type === "initial"
          ? availableStores
          : availableStores.filter((store) =>
              store.name.toString().toLowerCase().includes(searchText),
            );
    });
  };

  const handleModal = (isOpen) => {
    setState((draft) => {
      draft.isOpen = isOpen;
      draft.selectedStores = [];
    });
    HandleFilterStores("initial");
  };

  const handleStoreSelect = (id) => {
    setState((draft) => {
      draft.selectedStores = draft.selectedStores.includes(id)
        ? draft.selectedStores.filter((storeId) => storeId !== id)
        : [...draft.selectedStores, id];
    });
  };

  const handleAllSelect = (checked) => {
    setState((draft) => {
      draft.selectedStores = checked
        ? draft.filteredStores.map((store) => store.id)
        : [];
    });
  };

  const handleAddStore = async () => {
    setState((draft) => {
      draft.isSaveButton = true;
    });
    try {
      const res = await assignStoreToCoupons(
        { store_id: state.selectedStores },
        couponId,
      );
      if (res.success) {
        successMessage(res.message);
        handleModal(false);
        onRefresh();
      } else {
        throw new Error(res.message);
      }
    } catch (err) {
      errorMessage(err.response?.data?.message || err.message);
    } finally {
      setState((draft) => {
        draft.isSaveButton = false;
      });
    }
  };

  const DeleteStoreModal = (isDelete, storeId = null) => {
    setState((draft) => {
      draft.isDelete = isDelete;
      draft.storeId = storeId;
    });
  };

  const handleDeleteStore = async () => {
    setState((draft) => {
      draft.isDeleteButton = true;
    });
    try {
      const res = await deleteStoreFromCoupons(couponId, state.storeId);
      if (res.success) {
        successMessage(res.message || "Deleted Successfully");
        DeleteStoreModal(false);
        onRefresh();
      } else {
        throw new Error(res.message);
      }
    } catch (err) {
      errorMessage(err.response?.data?.message || err.message);
    } finally {
      setState((draft) => {
        draft.isDeleteButton = false;
      });
    }
  };

  return {
    state,
    HandleFilterStores,
    handleModal,
    handleStoreSelect,
    handleAllSelect,
    handleAddStore,
    DeleteStoreModal,
    handleDeleteStore,
  };
};
